// [CASH-MODE] Customer Cash QR: the QR a customer shows at the shop when paying by cash.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import axios from 'axios';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useReactToPrint } from 'react-to-print';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { customerNoOf } from '../../components/CustomerReportShell';
import qrLogo from '../../images/qrLogo.png';
import CashQCard, { buildCardQrDataUrl, CARD_PX_W, CARD_PX_H, CARD_MM_W, CARD_MM_H } from '../../components/CashQCard';

// Printed card always hides most of the mobile number.
const maskMobile = (mobile) => {
  const str = String(mobile || '').trim();
  if (!str) return 'N/A';
  if (str.length <= 4) return str;
  return `${'*'.repeat(str.length - 4)}${str.slice(-4)}`;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// A database id in the URL (links from other pages) is not shown in the search box.
const isDbId = (v) => /^[a-f0-9]{24}$/i.test(String(v || ''));

const CustomerCashQr = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCustomerId = searchParams.get('customerId') || '';

  const [searchInput, setSearchInput] = useState(isDbId(urlCustomerId) ? '' : urlCustomerId);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [qrInfo, setQrInfo] = useState(null); // { qrData, customerId, customerNo, customerName, customerMobile, qrVersion }
  const [qrImageUrl, setQrImageUrl] = useState('');
  const { can } = usePermissions();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const printRef = useRef(null);
  const exportRef = useRef(null); // off-screen 1011x638 copy of the card used for the print-ready files
  const [exporting, setExporting] = useState(false);

  const canPrint = can('customers.cashQr.print');
  const canRegenerate = can('customers.cashQr.regenerate');

  const applyQrResponse = useCallback(async (data) => {
    if (!data || !data.qrData) {
      setQrInfo(null);
      setQrImageUrl('');
      setError('QR data not available for this customer.');
      return;
    }
    setQrInfo(data);
    if (data.customerNo) setSearchInput((prev) => (prev && !isDbId(prev) ? prev : data.customerNo));
    setQrImageUrl(await buildCardQrDataUrl(data.qrData));
  }, []);

  const fetchQr = useCallback(async (idOrMobile) => {
    const value = String(idOrMobile || '').trim();
    if (!value) return;
    setLoading(true);
    setError('');
    setQrInfo(null);
    setQrImageUrl('');
    try {
      const response = await axios.get(
        `${API_BASE_URL}/cash/admin/customer/${encodeURIComponent(value)}/qr`,
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      await applyQrResponse(response.data?.data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) setError('Customer not found. Check the Customer No or mobile number.');
      else if (status === 401 || status === 403) setError('You are not allowed to view customer QR codes.');
      else setError(err.response?.data?.message || 'Failed to load the customer QR.');
    } finally {
      setLoading(false);
    }
  }, [applyQrResponse]);

  // Load from ?customerId= (also runs after a search updates the URL)
  useEffect(() => {
    if (urlCustomerId) {
      setSearchInput(isDbId(urlCustomerId) ? '' : urlCustomerId);
      fetchQr(urlCustomerId);
    }
  }, [urlCustomerId, fetchQr]);

  const handleSearch = () => {
    const value = searchInput.trim();
    if (!value) {
      setError('Enter a Customer No or mobile number.');
      return;
    }
    if (value === urlCustomerId) fetchQr(value);
    else setSearchParams({ customerId: value });
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: qrInfo ? `Cash_QR_${qrInfo.customerName || qrInfo.customerNo || 'customer'}` : 'Cash_QR',
  });

  const handleDownloadPdf = async () => {
    if (!canPrint) return;
    if (!qrInfo || !qrImageUrl) return;
    const doc = new jsPDF();
    const centerX = doc.internal.pageSize.getWidth() / 2;
    try {
      const logo = await loadImage(qrLogo);
      doc.addImage(logo, 'PNG', centerX - 10, 12, 20, 20);
    } catch (e) {
      // logo is optional
    }
    doc.setFontSize(20);
    doc.text('QPay Cash Payment QR', centerX, 42, { align: 'center' });
    doc.addImage(qrImageUrl, 'PNG', centerX - 55, 50, 110, 110);
    doc.setFontSize(14);
    doc.text(qrInfo.customerName || 'Customer', centerX, 172, { align: 'center' });
    doc.setFontSize(11);
    doc.text(`Customer ID: ${customerNoOf(qrInfo)}`, centerX, 181, { align: 'center' });
    doc.text(`Mobile: ${maskMobile(qrInfo.customerMobile)}`, centerX, 189, { align: 'center' });
    doc.setFontSize(12);
    doc.text('Show this QR to the shop when paying by cash', centerX, 202, { align: 'center' });
    doc.save(`Cash_QR_${qrInfo.customerNo || 'customer'}.pdf`);
  };

  // Print-ready Q-Card: PNG 1011x638 (85.6x54 mm @ 300 dpi) or PDF page exactly 85.6x54 mm, no margins.
  const handleDownloadQCard = async (format) => {
    if (!canPrint || !qrInfo || !qrImageUrl || !exportRef.current) return;
    setExporting(true);
    try {
      const imgs = Array.from(exportRef.current.querySelectorAll('img'));
      await Promise.all(imgs.map((im) => (im.complete ? null : new Promise((r) => { im.onload = r; im.onerror = r; }))));
      if (document.fonts && document.fonts.ready) await document.fonts.ready;
      const canvas = await html2canvas(exportRef.current, {
        scale: 1, width: CARD_PX_W, height: CARD_PX_H, windowWidth: CARD_PX_W, windowHeight: CARD_PX_H,
        backgroundColor: '#ffffff', useCORS: true, logging: false,
      });
      const base = `QCard_${qrInfo.customerNo || 'customer'}`;
      if (format === 'png') {
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = `${base}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: [CARD_MM_W, CARD_MM_H] });
        doc.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, CARD_MM_W, CARD_MM_H, undefined, 'FAST');
        doc.save(`${base}.pdf`);
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Could not create the Q-Card file.', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const handleRegenerate = async () => {
    if (!canRegenerate) return;
    if (!qrInfo?.customerId) return;
    setRegenerating(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/cash/admin/customer/${encodeURIComponent(qrInfo.customerId)}/qr/regenerate`,
        {},
        { headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` } }
      );
      await applyQrResponse(response.data?.data);
      setSnackbar({ open: true, message: response.data?.message || 'New QR generated. The old QR no longer works.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to regenerate QR.', severity: 'error' });
    } finally {
      setRegenerating(false);
      setConfirmOpen(false);
    }
  };

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      <BackButton />
      <br /><br />
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
        Customer Cash QR
      </Typography>

      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8} md={9}>
            <TextField
              fullWidth
              size="small"
              label="Customer No or Mobile Number"
              placeholder="Customer No or Mobile Number"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
              sx={{ height: 40, fontWeight: 700 }}
            >
              {loading ? 'Loading...' : 'Show QR'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && qrInfo && qrImageUrl && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
          {/* Printable card (same look as the customer app Q-Card) */}
          <Box ref={printRef} sx={{ p: 2, backgroundColor: 'white' }}>
            <CashQCard
              qrImage={qrImageUrl}
              customerName={qrInfo.customerName}
              customerNo={customerNoOf(qrInfo)}
              width={Math.min(CARD_PX_W, 520)}
            />
          </Box>
          {/* Off-screen full-size copy for PNG / PDF export */}
          <Box sx={{ position: 'fixed', left: -10000, top: 0, pointerEvents: 'none' }} aria-hidden>
            <CashQCard
              ref={exportRef}
              qrImage={qrImageUrl}
              customerName={qrInfo.customerName}
              customerNo={customerNoOf(qrInfo)}
              rounded={false}
              shadow={false}
            />
          </Box>

          <Typography variant="caption" color="text.secondary">
            QR version {qrInfo.qrVersion || 1}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            {canPrint && (
              <Button variant="contained" color="secondary" startIcon={<PictureAsPdfIcon />} onClick={handleDownloadPdf}>
                Download PDF
              </Button>
            )}
            {canPrint && (
              <Button variant="contained" color="success" startIcon={<DownloadIcon />} disabled={exporting} onClick={() => handleDownloadQCard('pdf')}>
                Download Q-Card (PDF)
              </Button>
            )}
            {canPrint && (
              <Button variant="outlined" color="success" startIcon={<DownloadIcon />} disabled={exporting} onClick={() => handleDownloadQCard('png')}>
                Download Q-Card (PNG)
              </Button>
            )}
            {canPrint && (
              <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrint()}>
                Print
              </Button>
            )}
            {canRegenerate && (
              <Button variant="outlined" color="error" startIcon={<AutorenewIcon />} onClick={() => setConfirmOpen(true)}>
                Regenerate QR
              </Button>
            )}
          </Box>
        </Box>
      )}

      <Dialog open={confirmOpen} onClose={() => !regenerating && setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Regenerate QR?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A new QR will be created for {qrInfo?.customerName || 'this customer'}. The old QR (printed card or
            screenshot) will stop working immediately and shops will not be able to accept cash with it. Use this
            when a card is lost or misused.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={regenerating}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRegenerate} disabled={regenerating}>
            {regenerating ? <CircularProgress size={22} color="inherit" /> : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerCashQr;
